import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {PublicService} from '../../../_services/public/public.service';

/** Selectors for the email field inside the onboarding iframe (Transfeera). */
const emailFieldSelectors = [
  '[data-testid="form-input-email"]',
  '[data-testid="form-input-personEmail"]',
];

@Component({
  selector: 'app-onboarding-kyc',
  templateUrl: './onboarding-kyc.component.html',
  styleUrls: ['./onboarding-kyc.component.css'],
  standalone: false,
})
export class OnboardingKycComponent implements OnInit, OnDestroy {
  @ViewChild('kycFrame') kycFrame?: {nativeElement: HTMLIFrameElement};

  @Input() identifier!: string;

  /**
   * When true and the iframe is same-origin (e.g. onboarding served via your proxy),
   * the parent will try to set display:none on the email field inside the iframe.
   * No effect if the iframe is cross-origin (contentDocument not accessible).
   */
  @Input() hideEmailField = false;

  /** Iframe src (e.g. Transfeera onboarding). If served from your domain (proxy), same-origin allows updating the iframe DOM from here. */
  kycUrl!: string;

  private hideEmailInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly publicService: PublicService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    if (!this.identifier) {
      void this.router.navigate(['/page-404']);
      return;
    }

    this.publicService.findOnboardingKyc(this.identifier).subscribe({
      next: onboarding => {
        if (onboarding && onboarding.kycUrl) {
          this.kycUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            onboarding.kycUrl,
          ) as string;
        } else {
          void this.router.navigate(['/page-404']);
        }
      },
      error: err => {
        console.error('Error fetching onboarding data:', err);
      },
    });
  }

  onIframeLoad(): void {
    if (!this.hideEmailField || !this.kycFrame?.nativeElement) return;
    this.startHidingEmailInIframe();
  }

  /**
   * When the iframe is same-origin (e.g. served via your proxy), we can access
   * its document and hide the email field. Poll until we have access and then
   * periodically apply hide (email may appear in a later step).
   */
  private startHidingEmailInIframe(): void {
    this.stopHidingEmailInIframe();
    const iframe = this.kycFrame?.nativeElement;
    if (!iframe) return;

    const hideMarker = 'data-onboarding-email-hidden';
    const intervalMs = 800;
    const maxAttempts = 120; // ~96s

    let attempts = 0;
    this.hideEmailInterval = setInterval(() => {
      attempts++;
      if (attempts > maxAttempts) {
        this.stopHidingEmailInIframe();
        return;
      }
      try {
        const doc = iframe.contentDocument;
        if (!doc) return; // cross-origin: contentDocument is null
        emailFieldSelectors.forEach(selector => {
          doc.querySelectorAll<HTMLElement>(selector).forEach(el => {
            if (el.getAttribute(hideMarker)) return;
            el.setAttribute(hideMarker, 'true');
            el.style.setProperty('display', 'none', 'important');
            const row =
              el.closest('.ant-form-item') ||
              el.closest('[class*="form-item"]') ||
              el.parentElement;
            if (row && row !== el) {
              row.setAttribute(hideMarker, 'true');
              (row as HTMLElement).style.setProperty(
                'display',
                'none',
                'important',
              );
            }
          });
        });
      } catch {
        // ignore (e.g. cross-origin after navigation)
      }
    }, intervalMs);
  }

  private stopHidingEmailInIframe(): void {
    if (this.hideEmailInterval) {
      clearInterval(this.hideEmailInterval);
      this.hideEmailInterval = null;
    }
  }

  ngOnDestroy(): void {
    this.stopHidingEmailInIframe();
  }
}
